import type { FC } from 'react';
import React from 'react';
import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import type { ArchPhotoProps } from './PhotoArch.types';
import { ASPECT_RATIO, OUTER_PIECE_THICKNESS } from './PhotoArch.constants';
import { getSsrHeroSizes } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';

const SSR_SIZES = getSsrHeroSizes('photoArch');

const INNER_GAP_THICKNESS_Y = 23;
const INNER_GAP_THICKNESS_X = 17;

const ArchBorderOverlay: FC<{
  outerArchColor: ArchPhotoProps['outerArchColor'];
  innerArchColor: ArchPhotoProps['innerArchColor'];
  borderRadius: number;
}> = ({ outerArchColor, innerArchColor, borderRadius }) => (
  <>
    <Box
      position="absolute"
      inset={0}
      border={`${pxToRem(OUTER_PIECE_THICKNESS)} solid`}
      borderColor={outerArchColor}
      borderTopLeftRadius={pxToRem(borderRadius)}
      borderTopRightRadius={pxToRem(borderRadius)}
      pointerEvents="none"
      zIndex={1}
    />
    <Box
      position="absolute"
      inset={0}
      backgroundColor="transparent"
      borderTop={pxToRem(INNER_GAP_THICKNESS_X + OUTER_PIECE_THICKNESS)}
      borderRight={pxToRem(INNER_GAP_THICKNESS_Y + OUTER_PIECE_THICKNESS)}
      borderBottom={pxToRem(INNER_GAP_THICKNESS_X + OUTER_PIECE_THICKNESS)}
      borderLeft={pxToRem(INNER_GAP_THICKNESS_Y + OUTER_PIECE_THICKNESS)}
      borderStyle="solid"
      borderColor={innerArchColor}
      borderTopLeftRadius={pxToRem(borderRadius)}
      borderTopRightRadius={pxToRem(borderRadius)}
      pointerEvents="none"
    />
  </>
);

export const ArchPhoto: FC<ArchPhotoProps> = props => {
  const { photo, containerDimensions, calculateBorderRadius, outerArchColor, innerArchColor, photoRenderer: PhotoRenderer } = props;
  const height = containerDimensions.height;
  const width = height * ASPECT_RATIO;

  return (
    <Box
      display="flex"
      position="relative"
      width="auto"
      height="100%"
      borderTopLeftRadius={pxToRem(calculateBorderRadius)}
      borderTopRightRadius={pxToRem(calculateBorderRadius)}
      aspectRatio={`${ASPECT_RATIO}`}
      overflow="hidden"
    >
      <PhotoRenderer
        photoUrl={photo.url}
        cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
        width={width}
        height={height}
        containerType="fixed-aspect"
        ssrSizes={SSR_SIZES}
      />
      <ArchBorderOverlay
        outerArchColor={outerArchColor}
        innerArchColor={innerArchColor}
        borderRadius={calculateBorderRadius}
      />
    </Box>
  );
};
